/** Noise-based one-shot recipes: whooshes, thuds, rustles, scuffs, crumbles, knocks, roars. */
import { SILENCE, ahr, perc, swell } from '../env';
import { clamp, nyquist } from '../util';
import { link, noiseSrc, type Synth } from './synth';

export type NoiseColor = 'white' | 'pink' | 'brown';

const BAND_COMP = 6;

function buf(s: Synth, c: NoiseColor): AudioBuffer {
  return c === 'white' ? s.kit.noise.white : c === 'pink' ? s.kit.noise.pink : s.kit.noise.brown;
}

export interface WhooshOpts {
  readonly dur: number;
  /** Band-pass sweep start / end (Hz). */
  readonly f0: number;
  readonly f1: number;
  readonly q?: number;
  readonly peak: number;
  /** Where in the duration the loudness peaks (0..1). */
  readonly peakAt?: number;
  readonly color?: NoiseColor;
  readonly pan?: number;
  /** Pan travels from `pan` to `panTo` over the duration. */
  readonly panTo?: number;
  readonly send?: number;
}

/** Band-pass-swept noise with an approach/pass loudness contour. */
export function whoosh(s: Synth, t: number, o: WhooshOpts): void {
  const ny = nyquist(s.kit.ctx);
  const moving = o.panTo !== undefined;
  const v = s.pool.voice(t, s.route, { pan: moving ? 0 : o.pan, send: o.send ?? 0.15 });
  const src = noiseSrc(v, s.kit, buf(s, o.color ?? 'pink'), t, t + o.dur + 0.05);
  const bp = v.filter('bandpass', o.f0, o.q ?? 1);
  bp.frequency.setValueAtTime(clamp(o.f0, 40, ny), t);
  bp.frequency.exponentialRampToValueAtTime(clamp(o.f1, 40, ny), t + o.dur);
  const g = v.gain();
  link(src, bp, g);
  if (moving) {
    const p = v.panner(o.pan ?? 0);
    if ('pan' in p) {
      p.pan.setValueAtTime(clamp(o.pan ?? 0, -1, 1), t);
      p.pan.linearRampToValueAtTime(clamp(o.panTo ?? 0, -1, 1), t + o.dur);
    }
    link(g, p, v.out);
  } else {
    g.connect(v.out);
  }
  const at = clamp(o.peakAt ?? 0.5, 0.05, 0.95);
  // A band-passed slice of noise keeps only a fraction of its energy; compensate so `peak`
  // reads roughly as output level (≈ 2× band RMS).
  swell(g.gain, t, o.peak * BAND_COMP, o.dur * at, o.dur * (1 - at));
}

export interface ThudOpts {
  readonly f0: number;
  readonly f1: number;
  readonly dur: number;
  readonly peak: number;
  /** Amount of low-passed brown "body" noise (0..1 relative to peak). */
  readonly noise?: number;
  readonly lp?: number;
  readonly pan?: number;
  readonly send?: number;
}

/** Low sine pitch-drop + muffled noise body: landings, knocks, dives. */
export function thud(s: Synth, t: number, o: ThudOpts): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.06 });
  const osc = v.osc('sine', o.f0);
  osc.frequency.setValueAtTime(o.f0, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.f1), t + o.dur * 0.6);
  const g = v.gain();
  link(osc, g, v.out);
  v.play(osc, t, perc(g.gain, t, o.peak, 0.003, o.dur));
  const amt = o.noise ?? 0.5;
  if (amt > 0) {
    const n = noiseSrc(v, s.kit, s.kit.noise.brown, t, t + o.dur + 0.05);
    const lp = v.filter('lowpass', o.lp ?? 500, 0.7);
    const ng = v.gain();
    link(n, lp, ng, v.out);
    perc(ng.gain, t, o.peak * amt, 0.002, o.dur * 0.6);
  }
}

export interface RustleOpts {
  readonly dur: number;
  readonly peak: number;
  readonly freq: number;
  readonly q?: number;
  /** Grain-modulator speed. */
  readonly rate?: number;
  readonly pan?: number;
  readonly send?: number;
}

/** Leafy rustle: band-passed noise amplitude-modulated by grainy crackle (audio-rate AM). */
export function rustle(s: Synth, t: number, o: RustleOpts): void {
  const v = s.pool.voice(t, s.route, { pan: o.pan, send: o.send ?? 0.12 });
  const end = t + o.dur + 0.05;
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, end);
  const bp = v.filter('bandpass', o.freq, o.q ?? 0.8);
  const am = v.gain(0.25);
  const mod = noiseSrc(v, s.kit, s.kit.noise.crackle, t, end, o.rate ?? 1.4);
  const modAmt = v.gain(2.2);
  link(mod, modAmt);
  modAmt.connect(am.gain);
  const g = v.gain();
  link(n, bp, am, g, v.out);
  ahr(g.gain, t, o.peak * 4, Math.min(0.03, o.dur * 0.2), o.dur * 0.25, o.dur * 0.55);
}

/** Sand scuff: hiss + grain crackle. */
export function sandScuff(s: Synth, t: number, dur: number, peak: number, pan = 0): void {
  const v = s.pool.voice(t, s.route, { pan, send: 0.08 });
  const end = t + dur + 0.05;
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, end);
  const hp = v.filter('highpass', 2600, 0.6);
  const g = v.gain();
  link(n, hp, g, v.out);
  ahr(g.gain, t, peak * 1.5, 0.01, dur * 0.2, dur * 0.7);
  const c = noiseSrc(v, s.kit, s.kit.noise.crackle, t, end, 1.2);
  const bp = v.filter('bandpass', 3000, 0.6);
  const cg = v.gain();
  link(c, bp, cg, v.out);
  ahr(cg.gain, t, peak * 3.5, 0.005, dur * 0.25, dur * 0.7);
}

/** Cartoony soft "poof": a low-passed puff closing down plus a round "bwomp". */
export function poof(s: Synth, t: number, peak: number): void {
  const v = s.pool.voice(t, s.route, { send: 0.3 });
  const n = noiseSrc(v, s.kit, s.kit.noise.pink, t, t + 0.65);
  const lp = v.filter('lowpass', 2400, 0.9);
  lp.frequency.setValueAtTime(2400, t);
  lp.frequency.exponentialRampToValueAtTime(280, t + 0.45);
  const g = v.gain();
  link(n, lp, g, v.out);
  perc(g.gain, t, peak, 0.015, 0.55);
  const osc = v.osc('sine', 210);
  osc.frequency.setValueAtTime(210, t);
  osc.frequency.exponentialRampToValueAtTime(92, t + 0.25);
  const og = v.gain();
  link(osc, og, v.out);
  v.play(osc, t, perc(og.gain, t, peak * 0.5, 0.01, 0.3));
}

/** Stone crumble: sparse gravel grains slowed down and band-passed, plus a dusty low rumble. */
export function crumble(s: Synth, t: number, dur: number, peak: number): void {
  const v = s.pool.voice(t, s.route, { send: 0.18 });
  const end = t + dur + 0.05;
  const c = noiseSrc(v, s.kit, s.kit.noise.crackle, t, end, 0.55);
  const bp = v.filter('bandpass', 1100, 0.8);
  const g = v.gain();
  link(c, bp, g, v.out);
  perc(g.gain, t, peak * 7, 0.005, dur);
  const b = noiseSrc(v, s.kit, s.kit.noise.brown, t, end);
  const lp = v.filter('lowpass', 600, 0.7);
  const bg = v.gain();
  link(b, lp, bg, v.out);
  perc(bg.gain, t, peak * 1.2, 0.01, dur * 0.5);
}

/** Hollow wooden knock: resonant sine pair with a contact click. */
export function knock(s: Synth, t: number, f: number, peak: number, pan = 0): void {
  const v = s.pool.voice(t, s.route, { pan, send: 0.15 });
  for (const [r, a, d] of [
    [1, 1, 0.22],
    [2.32, 0.35, 0.09],
    [4.1, 0.12, 0.04],
  ] as const) {
    const osc = v.osc('sine', f * r * 1.06);
    osc.frequency.setValueAtTime(f * r * 1.06, t);
    osc.frequency.exponentialRampToValueAtTime(f * r, t + 0.015);
    const g = v.gain();
    link(osc, g, v.out);
    v.play(osc, t, perc(g.gain, t, peak * a, 0.001, d));
  }
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, t + 0.04);
  const bp = v.filter('bandpass', f * 6, 2);
  const ng = v.gain();
  link(n, bp, ng, v.out);
  perc(ng.gain, t, peak * 0.3, 0.0005, 0.012);
}

/** Short filtered-noise crack (stone impacts). */
export function crack(s: Synth, t: number, freq: number, q: number, peak: number, dur: number, pan = 0): void {
  const v = s.pool.voice(t, s.route, { pan, send: 0.12 });
  const n = noiseSrc(v, s.kit, s.kit.noise.white, t, t + dur + 0.04);
  const bp = v.filter('bandpass', Math.min(freq, nyquist(s.kit.ctx)), q);
  const g = v.gain();
  link(n, bp, g, v.out);
  perc(g.gain, t, peak * BAND_COMP, 0.0006, dur);
}

/** Descending whistle with a little vibrato (falling off the world, doppler passes). */
export function whistleDrop(s: Synth, t: number, f0: number, f1: number, dur: number, peak: number, pan = 0, send = 0.35): void {
  const v = s.pool.voice(t, s.route, { send, pan });
  const osc = v.osc('sine', f0);
  osc.frequency.setValueAtTime(f0, t);
  osc.frequency.exponentialRampToValueAtTime(f1, t + dur);
  const vib = v.osc('sine', 5.5);
  const vibAmt = v.gain(f0 * 0.012);
  link(vib, vibAmt);
  vibAmt.connect(osc.frequency);
  const g = v.gain();
  link(osc, g, v.out);
  const end = ahr(g.gain, t, peak, 0.06, dur * 0.55, dur * 0.4);
  v.play(osc, t, end);
  v.play(vib, t, end);
}

export interface WashOpts {
  readonly color: NoiseColor;
  readonly lp0: number;
  readonly lp1: number;
  readonly attack: number;
  readonly hold: number;
  readonly release: number;
  readonly peak: number;
  /** End with a sharp (70 ms exponential, click-free) cut instead of a release. */
  readonly cut?: boolean;
  readonly send?: number;
}

/** Low-passed noise swell — storm roars, muffled cloud banks. */
export function wash(s: Synth, t: number, o: WashOpts): void {
  const ny = nyquist(s.kit.ctx);
  const v = s.pool.voice(t, s.route, { send: o.send ?? 0.2 });
  const total = o.attack + o.hold + (o.cut ? 0.08 : o.release);
  const n = noiseSrc(v, s.kit, buf(s, o.color), t, t + total + 0.05);
  const lp = v.filter('lowpass', o.lp0, 0.8);
  lp.frequency.setValueAtTime(clamp(o.lp0, 40, ny), t);
  lp.frequency.exponentialRampToValueAtTime(clamp(o.lp1, 40, ny), t + o.attack + o.hold);
  const g = v.gain();
  link(n, lp, g, v.out);
  const p = g.gain;
  if (o.cut) {
    p.setValueAtTime(0, t);
    p.linearRampToValueAtTime(SILENCE, t + 0.002);
    p.exponentialRampToValueAtTime(o.peak, t + o.attack);
    p.setValueAtTime(o.peak, t + o.attack + o.hold);
    p.exponentialRampToValueAtTime(SILENCE, t + o.attack + o.hold + 0.07);
    p.linearRampToValueAtTime(0, t + o.attack + o.hold + 0.075);
  } else {
    ahr(p, t, o.peak, o.attack, o.hold, o.release);
  }
}
